<template>
  <div v-if="getConfirmedBet" class="bet-confirmed-panel">

    <div class="confirmed-code-block">
      <span class="confirmed-title text-uppercase">
        {{ getConfirmedBet.is_booked ? $t('betBooked') : $t('betAccepted') }}
      </span>
      <span class="confirmed-code">Id: {{ getConfirmedBet.bet_code }}</span>
      <button class="confirmed-copy-btn" @click="copyCode(getConfirmedBet.bet_code)" :title="$t('copy')">
        <v-icon size="12">mdi-content-copy</v-icon>
      </button>
    </div>

    <div class="confirmed-odds-list">
      <template v-for="(group, gIdx) in groupedConfirmedOdds">

        <!-- BetBuilder group -->
        <div v-if="group.is_bet_builder" :key="'bb-'+gIdx" class="confirmed-odd-row">
          <div class="confirmed-odd-event">
            <BetBuilderBadge default-text="BB" class="conf-bb-badge" />
            <span class="confirmed-event-name">{{ group.odds[0].event_description }}</span>
          </div>
          <div class="conf-bb-timeline">
            <div
              v-for="(odd, idx) in group.odds"
              :key="odd.unique_id"
              class="conf-bb-row"
              :class="{ 'conf-bb-row--last': idx === group.odds.length - 1 }"
            >
              <div class="conf-bb-dot"></div>
              <div class="conf-bb-content">
                <span class="conf-bb-outcome">{{ getOutcomeName(odd) }}</span>
                <span class="conf-bb-market">{{ odd.market_alias || odd.market_name }}</span>
              </div>
            </div>
            <div class="conf-bb-combo-row">
              <span class="confirmed-odd-value conf-bb-combined-odd">{{ getBBCombinedOdd(group) | odds_type_filter }}</span>
            </div>
          </div>
        </div>

        <!-- Leg normale -->
        <div v-else :key="'leg-'+gIdx" class="confirmed-odd-row">
          <div class="confirmed-odd-event">
            <img
              v-if="group.odds[0].sport && group.odds[0].sport.id"
              :src="sportPath + group.odds[0].sport.id + '.svg'"
              width="12" height="12"
              class="confirmed-sport-icon"
            >
            <span class="confirmed-event-name">{{ group.odds[0].event_description }}</span>
            <span v-if="group.odds[0].type === 'live'" class="live-badge">LIVE</span>
          </div>
          <div class="confirmed-odd-detail">
            <span class="confirmed-market-name">
              {{ group.odds[0].market_alias || group.odds[0].market_name }}
              <span v-if="getOddLine(group.odds[0])" class="confirmed-specifier">{{ getOddLine(group.odds[0]) }}</span>
            </span>
            <div class="confirmed-odd">
              <span class="confirmed-selection">{{ getOutcomeName(group.odds[0]) }}</span>
              <div class="confirmed-odd-right">
                <template v-if="group.odds[0].old_odd">
                  <span class="confirmed-odd-value confirmed-odd-value--old">{{ group.odds[0].old_odd | odds_type_filter }}</span>
                  <v-icon x-small class="confirmed-odd-arrow">mdi-arrow-right</v-icon>
                  <span class="confirmed-odd-value confirmed-odd-value--changed">{{ group.odds[0].outcome_odd_value | odds_type_filter }}</span>
                </template>
                <span v-else class="confirmed-odd-value">{{ group.odds[0].outcome_odd_value | odds_type_filter }}</span>
              </div>
            </div>
          </div>
        </div>

      </template>
    </div>

    <div class="confirmed-summary-card">
      <div class="summary-row">
        <span class="summary-label">{{ $t(getConfirmedBet.type) }}</span>
        <span class="summary-value" v-if="getConfirmedBet.odds_total > 0">
          {{ getConfirmedBet.odds_total | round_digits(2) }}
        </span>
      </div>
      <div class="summary-row">
        <span class="summary-label">{{ $t('amount') }}</span>
        <span class="summary-value">
          {{ getSettings.currencySymbol }} {{ getConfirmedBet.amount | round_digits(2) }}
        </span>
      </div>
      <div class="summary-row" v-if="getConfirmedBet.bonus_percentage > 0">
        <span class="summary-label">{{ $t('bonus') }}</span>
        <span class="summary-value summary-value--green">% {{ getConfirmedBet.bonus_percentage }}</span>
      </div>
      <div class="summary-row summary-row--winning">
        <span class="summary-label">{{ $t('winning') }} {{ $t('total') }}</span>
        <span class="summary-value summary-value--highlight">
          {{ getSettings.currencySymbol }} {{ getConfirmedBet.winning_total | round_digits(2) }}
        </span>
      </div>
    </div>

    <div class="confirmed-actions">

      <div
          v-if="!getConfirmedBet.is_booked"
          class="confirmed-action-btn icon-only-btn confirmed-action-btn--secondary"
          @click="onPrint"
          :title="$t('print')"
      >
        <v-icon small>mdi-printer</v-icon>
      </div>

      <div
          v-if="getConfirmedBet.is_booked"
          class="confirmed-action-btn icon-only-btn confirmed-action-btn--secondary"
          @click="onShareLink"
          :title="$t('share') || 'Condividi Link'"
      >
        <v-icon small>mdi-export-variant</v-icon>
      </div>

      <div
          v-if="!getConfirmedBet.is_booked"
          class="confirmed-action-btn icon-only-btn confirmed-action-btn--secondary"
          @click="onShareImage"
          :title="$t('shareImage') || 'Condividi Immagine'"
          :class="{ 'loading-btn': loadingImage }"
      >
        <v-progress-circular v-if="loadingImage" indeterminate size="16" width="2" color="var(--text-platform)"></v-progress-circular>
        <v-icon v-else small>mdi-export-variant</v-icon>
      </div>

      <div
          v-if="!getConfirmedBet.is_booked"
          class="confirmed-action-btn icon-only-btn confirmed-action-btn--secondary"
          @click="onDownload"
          :title="$t('download')"
      >
        <v-icon small>mdi-download</v-icon>
      </div>

      <div
          class="confirmed-action-btn icon-only-btn confirmed-action-btn--secondary"
          @click="onNewBet"
          :title="$t('newBet')"
      >
        <v-icon small>mdi-delete-outline</v-icon>
      </div>

      <div class="confirmed-action-btn cta-primary confirmed-action-btn--primary" @click="onReloadBet">
        <v-icon small class="mr-1">mdi-reload</v-icon>
        <span>{{ $t('reloadBet') || 'Riporta Eventi' }}</span>
      </div>

    </div>

    <v-snackbar v-model="snackbar" :timeout="2500" :color="snackbarColor" top>
      {{ snackbarText }}
    </v-snackbar>

    <BetShareReceipt
        ref="shareReceipt"
        :bet="getConfirmedBet"
        :currency-symbol="getSettings.currencySymbol"
        @share-end="loadingImage = false"
    />

    <PrintReceiptBet
        ref="printReceipt"
        :bet="getConfirmedBet"
        :currency-symbol="getSettings.currencySymbol"
    />

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BetShareReceipt from './BetShareReceipt.vue';
import PrintReceiptBet from '../generic/PrintReceiptBet.vue';
import BetBuilderBadge from '@/components/badges/BetBuilderBadge.vue';

export default {
  name: 'BetConfirmedMarketView',
  components: { BetShareReceipt, PrintReceiptBet, BetBuilderBadge },

  data() {
    return {
      // Unifichiamo lo snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      loadingImage: false, // Stato di caricamento per la generazione immagine
      sportPath: process.env.VUE_APP_URL_CLOUDFRONT + '/svg/sports/icons-color/',
    };
  },

  computed: {
    groupedConfirmedOdds() {
      const odds = this.getConfirmedBet?.odds || [];
      const map = new Map();
      for (const odd of odds) {
        const key = odd.bet_builder_id || odd.unique_id;
        if (!map.has(key)) {
          map.set(key, { key, is_bet_builder: !!odd.bet_builder_id, odds: [] });
        }
        map.get(key).odds.push(odd);
      }
      return Array.from(map.values());
    },
    ...mapGetters({
      getConfirmedBet:     'cart/getConfirmedBet',
      getSettings:         'cart/getSettings',
      getPlatformSettings: 'settings/getPlatformSettings',
      getShortCode:        'settings/getShortCode',
      getOddsType:         'odds/getOddsType',
      getType:             'user/getType',
      getId:               'user/getId',
      getWebsite:          'settings/getWebsite',
      getB2bEnabled:       'settings/getB2bEnabled',
    }),
  },

  methods: {
    ...mapActions({
      resetCart:         'cart/resetCart',
      clearConfirmedBet: 'cart/clearConfirmedBet',
    }),

    // Mostra snackbar generico
    showNotify(text, color = '') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    async onNewBet() {
      this.$root.$emit('cart_handling', true);
      await this.resetCart();
      await this.clearConfirmedBet();
    },

    async onReloadBet() {
      await this.clearConfirmedBet();
    },

    onPrint() {
      if (!this.getConfirmedBet || this.getConfirmedBet.is_booked) return;
      if (this.getB2bEnabled) {
        this.$refs.printReceipt.triggerPrint();
        return;
      }
      const code = this.getConfirmedBet.bet_code;
      const base = this.getPlatformSettings.print_endpoint + code;
      const url = this.getShortCode
          ? `${base}&login=${this.getType}&ancestors=${this.getId}&type_odd=${this.getOddsType}`
          : `${base}&type_odd=${this.getOddsType}`;
      window.open(url, '_blank', 'height=600,width=350');
    },
    onDownload() {
      if (!this.getConfirmedBet || this.getConfirmedBet.is_booked) return;
      if (this.getB2bEnabled) {
        this.$refs.printReceipt.triggerDownload();
        return;
      }
      const code = this.getConfirmedBet.bet_code;
      const base = this.getPlatformSettings.print_endpoint + code;
      const url = this.getShortCode
          ? `${base}&login=${this.getType}&ancestors=${this.getId}&type_odd=${this.getOddsType}&autoPdf=true`
          : `${base}&type_odd=${this.getOddsType}&autoPdf=true`;
      window.open(url, '_blank', 'height=600,width=350');
    },

    // Vecchio onShare rinominato, per condividere il LINK (solo booked)
    async onShareLink() {
      if (!this.getConfirmedBet || !this.getConfirmedBet.bet_code) return;

      const baseUrl = this.getWebsite || window.location.origin;
      // Nota: solitamente le giocate booked vanno in una rotta specifica, es /booking/
      const shareUrl = `${baseUrl}/betting/?bookingCode=${this.getConfirmedBet.bet_code}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: this.$t('shareBet') || 'Condividi Scommessa',
            url: shareUrl
          });
        } catch (e) {
          console.error('Condivisione link fallita', e);
        }
      } else {
        this.copyCode(shareUrl);
      }
    },

    async onShareImage() {
      if (this.loadingImage || !this.getConfirmedBet) return;
      this.loadingImage = true;
      await this.$refs.shareReceipt.triggerShare();
    },

    async copyCode(text) {
      if (!text) return;
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(text);
        } else {
          this._fallbackCopy(text);
        }
        this.showNotify(this.$t('codeCopiedToClipboard'));
      } catch {
        this._fallbackCopy(text);
        this.showNotify(this.$t('codeCopiedToClipboard'));
      }
    },

    _fallbackCopy(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    },

    getBBCombinedOdd(group) {
      const main = group.odds.find(o => parseFloat(o.outcome_odd_value) !== 1);
      return main ? main.outcome_odd_value : (group.odds[0] ? group.odds[0].outcome_odd_value : 0);
    },
    getOddLine(odd) {
      if (odd.line && odd.line !== '') return odd.line;
      if (odd.baseLine && odd.baseLine !== '') return odd.baseLine;
      if (odd.specifiers && odd.specifiers !== '') {
        const part = odd.specifiers.split(' ')[0];
        return isNaN(part.charAt(0)) && part.charAt(0) !== '-' && part.charAt(0) !== '+' ? null : part;
      }
      return null;
    },

    getOutcomeName(odd) {
      const name = odd.outcome_name;
      if (!name) return odd.outcome_alias || '';
      if (name === 'X') return odd.outcome_alias || odd.outcome_name;
      const lower = name.toLowerCase();
      const c = odd.competitors;
      if (lower === 'local' && c && c[0]) return c[0].name;
      if (lower === 'visitante' && c && c[1]) return c[1].name;
      if ((name === '1' || name === '2') && odd.unique_id !== '128' && c) {
        const found = c.find(function(t) { return t.position === name; });
        if (found) return found.name;
      }
      return odd.outcome_alias || name;
    },
  },
};
</script>

<style scoped>
/* ── Panel Principale (Invariato) ────────────────────────────────── */
.bet-confirmed-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 0 8px;
  animation: confirmedFadeIn 0.3s ease;
}

@keyframes confirmedFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Header e Codice (Invariati) ─────────────────────────────────── */
.confirmed-code-block {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 10px 16px;
  background: var(--color-primary-gradient);
  border: 1px solid var(--color-primary-light-op-50);
  border-radius: 8px;
}
.confirmed-title { font-size: var(--font-size-2xl); font-weight: 500; color: var(--text-platform); }
.confirmed-code { font-size: var(--font-size-4xl); font-weight: 800; color: var(--text-platform); }
.confirmed-copy-btn { background: none; border: none; cursor: pointer; padding: 2px 4px; opacity: 0.5; color: var(--text-platform); display: flex; align-items: center; }
.confirmed-copy-btn:hover { opacity: 1; }
.confirmed-copy-btn .v-icon { color: var(--text-platform); }

/* ── Riepilogo e Lista Quote (Invariati) ──────────────────────────── */
.confirmed-summary-card { background: var(--bg-card); border-radius: 6px; overflow: hidden; }
.summary-row { display: flex; align-items: center; justify-content: space-between; padding: 6px 12px; font-size: var(--font-size-2xl); min-height: 30px; }
.summary-row + .summary-row { border-top: 1px solid rgba(255, 255, 255, 0.05); }
.summary-label { color: var(--text-op-50-platform); font-weight: 500; }
.summary-value { font-weight: 600; color: var(--text-platform); }
.summary-value--green { color: #22c55e; }
.summary-row--winning { background: rgba(255, 255, 255, 0.03); border-top: 1px solid rgba(255, 255, 255, 0.08) !important; }
.summary-row--winning .summary-label { color: var(--text-platform); font-weight: 600; }
.summary-value--highlight { font-size: var(--font-size-3xl); font-weight: 700; color: var(--color-primary, #22c55e); }
.confirmed-odds-list { display: flex; flex-direction: column; gap: 4px; }

/* ── BetBuilder nel panel confermato ── */
.conf-bb-badge { flex-shrink: 0; padding: 2px 5px; border-radius: 4px; font-size: var(--font-size-xs); font-weight: 800; line-height: 13px; color: var(--bg-body); background: var(--color-primary-light); }
.conf-bb-timeline { display: flex; flex-direction: column; gap: 8px; padding: 6px 0 4px 16px; position: relative; margin-top: 4px; }
.conf-bb-row { position: relative; display: flex; flex-direction: column; gap: 1px; }
.conf-bb-row:not(.conf-bb-row--last)::before { content: ''; position: absolute; left: -9px; top: 5px; bottom: -8px; width: 1px; background: var(--color-primary-light, #21a8f7); opacity: 0.4; }
.conf-bb-dot { position: absolute; left: -12px; top: 4px; width: 6px; height: 6px; border-radius: 50%; background: var(--color-primary-light, #21a8f7); box-shadow: 0 0 4px rgba(33,168,247,0.6); z-index: 1; }
.conf-bb-content { display: flex; flex-direction: column; gap: 1px; }
.conf-bb-outcome { font-size: var(--font-size-xl); font-weight: 600; color: var(--text-platform); }
.conf-bb-market { font-size: var(--font-size-md); color: var(--text-op-50-platform); text-transform: uppercase; }
.conf-bb-combo-row { display: flex; justify-content: flex-end; margin-top: 4px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.07); }
.conf-bb-combined-odd { color: var(--color-primary-light) !important; background: var(--color-primary-op) !important; }
.confirmed-odd-row { background: var(--bg-card); border-radius: 6px; padding: 8px 12px; display: flex; flex-direction: column; gap: 4px; }
.confirmed-odd-event { display: flex; align-items: center; gap: 6px; }
.confirmed-sport-icon { flex-shrink: 0; opacity: 0.75; }
.confirmed-event-name { font-size: var(--font-size-xl); font-weight: 600; color: var(--text-platform); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.live-badge { font-size: var(--font-size-xs); font-weight: 700; background: #ef4444; color: #fff; padding: 1px 4px; border-radius: 3px; flex-shrink: 0; }
.confirmed-odd-detail { display: flex; align-items: flex-start; flex-direction: column; justify-content: space-between; gap: 8px; }
.confirmed-market-name { font-size: var(--font-size-md); color: var(--text-op-50-platform); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: flex; align-items: center; gap: 4px; }
.confirmed-specifier { color: var(--text-platform); font-weight: 700; flex-shrink: 0; }
.confirmed-odd { display: flex; align-items: center; gap: 6px; flex-shrink: 0; width: 100%; justify-content: space-between; }
.confirmed-odd-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.confirmed-selection { font-size: var(--font-size-xl); font-weight: 600; color: var(--text-platform); }
.confirmed-odd-value { font-size: var(--font-size-xl); font-weight: 700; color: var(--color-primary, #22c55e); background: var(--color-primary-op); border-radius: 4px; padding: 2px 6px; }
.confirmed-odd-value--old { font-size: var(--font-size-xl); font-weight: 600; color: var(--text-op-50-platform); background: transparent; text-decoration: line-through; padding: 2px 4px; }
.confirmed-odd-value--changed { font-size: var(--font-size-xl); font-weight: 700; color: #f59e0b; background: rgba(245, 158, 11, 0.12); border-radius: 4px; padding: 2px 6px; }
.confirmed-odd-arrow { color: var(--text-op-50-platform) !important; font-size: var(--font-size-md) !important; }

/* ── Bottoni Azione (Aggiornati) ────────────────────────────────── */
.confirmed-actions {
  display: flex;
  gap: 6px;
  padding-top: 4px;
  flex-wrap: nowrap;
}

.confirmed-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  border-radius: 6px;
  font-size: var(--font-size-xl);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
}

.icon-only-btn {
  flex: 0 0 38px;
  width: 38px;
  padding: 0;
}

.icon-only-btn .v-icon {
  margin: 0 !important;
}

/* Stato loading sul bottone */
.loading-btn {
  pointer-events: none;
  opacity: 0.7;
}

.cta-primary {
  flex: 1;
  min-width: 120px;
}

.cta-primary span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.confirmed-action-btn--secondary {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: var(--text-platform);
}

.confirmed-action-btn--secondary:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.35);
}

.confirmed-action-btn--primary {
  background: var(--btn-background);
  border: none;
  color: var(--btn-text);
}
.confirmed-action-btn--primary .v-icon{
  color: var(--btn-text)!important;
}

.confirmed-action-btn--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0, 200, 83, 0.35);
}

</style>